import { useEffect, useState, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { client } from '../lib/sanity'
import CommandPalette from './ui/CommandPalette'

const smoothScrollToSection = (id) => {
  const element = document.getElementById(id)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

const NAV_CACHE_KEY = 'nav-cache-v1'

const FALLBACK_NAV = {
  title: 'Portfolio',
  customLinks: [
    { show: true, slug: 'about', label: 'About' },
    { show: true, slug: 'projects', label: 'Projects' },
    { show: true, slug: 'blog', label: 'Blog' },
    { show: true, slug: 'contact', label: 'Contact' },
  ],
}

function readCachedNav() {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(NAV_CACHE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

const PREFETCH_TYPE_BY_SLUG = {
  about: 'about',
  projects: 'project',
  blog: 'blog',
  publications: 'publication',
  images: 'imageGallery',
  videos: 'videoGallery',
  contact: 'contact',
  notes: 'note',
}

export default function Navbar() {
  const [nav, setNav] = useState(() => readCachedNav() || FALLBACK_NAV)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [openDropdown, setOpenDropdown] = useState(null)
  const [commandOpen, setCommandOpen] = useState(false)
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'dark'
    return localStorage.getItem('theme') || 'dark'
  })
  const [isZoomed, setIsZoomed] = useState(() => {
    if (typeof window === 'undefined') return true
    return localStorage.getItem('isZoomed') !== 'false'
  })
  const [isCompact, setIsCompact] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const prefetchTimeoutRef = useRef(null)
  const location = useLocation()

  const getBreadcrumbs = () => {
    const path = location.pathname
    if (path === '/') return []
    const segments = path.split('/').filter(Boolean)
    return segments.map((segment, idx) => ({
      label: segment.charAt(0).toUpperCase() + segment.slice(1),
      path: '/' + segments.slice(0, idx + 1).join('/')
    }))
  }

  const handleLinkHover = (slug) => {
    const type = PREFETCH_TYPE_BY_SLUG[slug]
    if (!type) return
    prefetchTimeoutRef.current = setTimeout(() => {
      client.fetch(`*[_type == $type][0...1]{_id}`, { type }).catch(() => {})
    }, 200)
  }

  const handleLinkLeave = () => {
    if (prefetchTimeoutRef.current) clearTimeout(prefetchTimeoutRef.current)
  }

  const handleAnchorClick = (e, href) => {
    if (href.startsWith('#')) {
      e.preventDefault()
      smoothScrollToSection(href.slice(1))
      setIsMenuOpen(false)
    }
  }

  useEffect(() => {
    if (typeof document === 'undefined') return
    const root = document.documentElement
    if (theme === 'dark') root.classList.add('dark')
    else root.classList.remove('dark')
    try { localStorage.setItem('theme', theme) } catch {}
  }, [theme])

  useEffect(() => {
    if (typeof document === 'undefined') return
    const root = document.documentElement
    if (isZoomed) root.classList.add('text-lg')
    else root.classList.remove('text-lg')
    try { localStorage.setItem('isZoomed', isZoomed) } catch {}
  }, [isZoomed])

  useEffect(() => {
    let ticking = false
    const ENTER = 80
    const EXIT = 40
    const onScroll = () => {
      const y = window.scrollY || 0
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsCompact(prev => (prev ? y > EXIT : y > ENTER))
          setIsScrolled(y > 10)
          ticking = false
        })
        ticking = true
      }
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    client.fetch(`*[_type == "navbar"][0]`).then((data) => {
      if (!data) return
      setNav(data)
      try { localStorage.setItem(NAV_CACHE_KEY, JSON.stringify(data)) } catch {}
    }).catch(() => {})
  }, [])

  useEffect(() => {
    return () => {
      if (prefetchTimeoutRef.current) clearTimeout(prefetchTimeoutRef.current)
    }
  }, [])

  useEffect(() => {
    if (openDropdown === null) return
    const onDocClick = (e) => {
      if (!e.target.closest('[data-nav-dropdown-root]')) setOpenDropdown(null)
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpenDropdown(null)
    }
    document.addEventListener('click', onDocClick)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('click', onDocClick)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [openDropdown])

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setCommandOpen((v) => !v)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <nav
      role="navigation"
      aria-label="Main"
      className={`sticky top-0 left-0 z-50 w-full h-16 border-b-2 border-black bg-white text-black transition-all duration-300 dark:border-white dark:bg-zinc-950 dark:text-white ${
        isScrolled ? 'shadow-brutal-sm' : ''
      }`}
    >
      <div className={`max-w-7xl mx-auto h-full px-4 flex flex-row justify-between items-center transition-all duration-300 ${isCompact ? 'py-2 scale-[0.97]' : 'py-4 scale-100'}`}>
        <LogoSection isCompact={isCompact} nav={nav} />
        <DesktopNavigation nav={nav} location={location} openDropdown={openDropdown} setOpenDropdown={setOpenDropdown} handleLinkHover={handleLinkHover} handleLinkLeave={handleLinkLeave} handleAnchorClick={handleAnchorClick} isCompact={isCompact} />
        <BreadcrumbNavigation breadcrumbs={getBreadcrumbs()} />
        <DesktopControls isCompact={isCompact} theme={theme} setTheme={setTheme} isZoomed={isZoomed} setIsZoomed={setIsZoomed} setCommandOpen={setCommandOpen} />
        <MobileMenuButton isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      </div>
      <MobileMenu isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} nav={nav} location={location} openDropdown={openDropdown} setOpenDropdown={setOpenDropdown} handleAnchorClick={handleAnchorClick} theme={theme} setTheme={setTheme} setCommandOpen={setCommandOpen} />
      <CommandPalette
        open={commandOpen}
        onClose={() => setCommandOpen(false)}
        theme={theme}
        setTheme={setTheme}
        isZoomed={isZoomed}
        setIsZoomed={setIsZoomed}
      />
    </nav>
  )
}

function LogoSection({ isCompact, nav }) {
  return (
    <div className="space-x-8 flex items-center">
      <motion.div
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      >
        <Link to="/" className={`font-serif font-extrabold tracking-wider text-black dark:text-white transition-all duration-300 inline-block ${isCompact ? 'text-xl' : 'text-2xl'}`}>
          {nav.title || 'MyPortfolio'}
        </Link>
      </motion.div>
    </div>
  )
}

function DesktopNavigation({ nav, location, openDropdown, setOpenDropdown, handleLinkHover, handleLinkLeave, handleAnchorClick, isCompact }) {
  return (
    <div className="hidden md:flex space-x-4">
      {nav.customLinks?.filter(link => link.show).map((link, i) => {
        const isActive = location.pathname === '/' + link.slug
        const hasChildren = link.children?.length > 0

        return (
          <NavLink
            key={i}
            link={link}
            isActive={isActive}
            hasChildren={hasChildren}
            index={i}
            openDropdown={openDropdown}
            setOpenDropdown={setOpenDropdown}
            handleLinkHover={handleLinkHover}
            handleLinkLeave={handleLinkLeave}
            handleAnchorClick={handleAnchorClick}
          />
        )
      })}
    </div>
  )
}

function NavLink({ link, isActive, hasChildren, index, openDropdown, setOpenDropdown, handleLinkHover, handleLinkLeave, handleAnchorClick }) {
  const isOpen = openDropdown === index
  const dropdownId = `nav-dropdown-${index}`

  return (
    <div className="relative group" data-nav-dropdown-root>
      <motion.div
        onHoverStart={() => {
          if (hasChildren) setOpenDropdown(index)
          handleLinkHover(link.slug)
        }}
        onHoverEnd={() => {
          setOpenDropdown(null)
          handleLinkLeave()
        }}
        className="flex items-center"
      >
        <Link
          to={`/${link.slug}`}
          onClick={(e) => handleAnchorClick(e, link.slug)}
          className={`relative px-2 py-1 font-mono transition-colors duration-150 capitalize group inline-flex items-center gap-1 hover:text-accent ${isActive ? 'text-accent font-semibold' : ''}`}
          aria-current={isActive ? 'page' : undefined}
        >
          {link.label}
        </Link>
        {hasChildren && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setOpenDropdown(isOpen ? null : index)
            }}
            aria-expanded={isOpen}
            aria-haspopup="true"
            aria-controls={dropdownId}
            aria-label={`${link.label} submenu`}
            className="p-1 transition-colors hover:text-accent"
          >
            <ChevronDownIcon
              className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            />
          </button>
        )}
        {isActive && (
          <motion.span
            layoutId="active-underline"
            className="absolute left-0 right-0 -bottom-1 h-0.5 bg-accent"
            transition={{ type: 'spring', stiffness: 500, damping: 40 }}
          />
        )}
        <motion.span
          className="absolute left-0 right-0 -bottom-1 h-0.5 bg-accent opacity-0 group-hover:opacity-100"
          transition={{ duration: 0.2 }}
        />
      </motion.div>

      {hasChildren && (
        <AnimatePresence>
          {isOpen && (
            <NavDropdown id={dropdownId} link={link} handleAnchorClick={handleAnchorClick} setOpenDropdown={setOpenDropdown} />
          )}
        </AnimatePresence>
      )}
    </div>
  )
}

function NavDropdown({ id, link, handleAnchorClick, setOpenDropdown }) {
  return (
    <motion.div
      id={id}
      role="menu"
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ type: 'spring', stiffness: 500, damping: 32 }}
      className="absolute top-full mt-2 left-0 min-w-[160px] overflow-hidden z-50 bg-white dark:bg-zinc-950 border-2 border-black dark:border-white shadow-brutal"
    >
      {link.children.map((child, cidx) => (
        <Link
          key={cidx}
          to={`/${child.slug}`}
          onClick={(e) => {
            handleAnchorClick(e, child.slug)
            setOpenDropdown(null)
          }}
          className="block px-4 py-2.5 font-mono text-sm capitalize text-black dark:text-white transition-colors hover:bg-accent hover:text-black"
        >
          {child.label}
        </Link>
      ))}
    </motion.div>
  )
}

function BreadcrumbNavigation({ breadcrumbs }) {
  if (breadcrumbs.length === 0) return null

  return (
    <div className="hidden lg:flex items-center text-sm font-mono font-medium text-black dark:text-white gap-2 px-4 py-2 border-2 border-black dark:border-white">
      <Link to="/" className="hover:text-accent transition-colors font-semibold">Home</Link>
      {breadcrumbs.map((crumb, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <span className="text-zinc-400 dark:text-zinc-600">/</span>
          <Link
            to={crumb.path}
            className={`capitalize transition-colors font-medium ${idx === breadcrumbs.length - 1 ? 'text-accent font-bold' : 'hover:text-accent'}`}
            aria-current={idx === breadcrumbs.length - 1 ? 'page' : undefined}
          >
            {crumb.label}
          </Link>
        </div>
      ))}
    </div>
  )
}

function DesktopControls({ isCompact, theme, setTheme, isZoomed, setIsZoomed, setCommandOpen }) {
  return (
    <div className="hidden md:flex items-center gap-2">
      <CommandPaletteTrigger isCompact={isCompact} onClick={() => setCommandOpen(true)} />
      <button
        onClick={() => setIsZoomed(!isZoomed)}
        aria-label="Toggle text size"
        title={isZoomed ? "Normal size" : "Increase text size"}
        className={`px-2 py-1.5 font-mono text-xs border-2 transition-all duration-300 ${isCompact ? 'scale-95' : 'scale-100'} ${isZoomed ? 'bg-accent text-black border-black dark:border-white' : 'border-black dark:border-white text-black dark:text-white hover:bg-accent hover:text-black'}`}
      >
        A+
      </button>
      <ThemeToggle theme={theme} setTheme={setTheme} isCompact={isCompact} />
    </div>
  )
}

function CommandPaletteTrigger({ isCompact, onClick }) {
  const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform || navigator.userAgent)
  return (
    <button
      onClick={onClick}
      aria-label="Open command palette"
      title="Open command palette"
      className={`inline-flex items-center gap-2 border-2 border-black dark:border-white bg-white dark:bg-zinc-950 text-black dark:text-white font-mono text-xs transition-all duration-300 hover:bg-accent hover:text-black ${isCompact ? 'px-2 py-1.5 scale-95' : 'px-3 py-2 scale-100'}`}
    >
      <span>{isMac ? '⌘K' : 'Ctrl K'}</span>
    </button>
  )
}

function ThemeToggle({ theme, setTheme, isCompact }) {
  return (
    <motion.button
      onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      aria-label="Toggle dark mode"
      title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
      className={`inline-flex items-center gap-2 border-2 border-black dark:border-white bg-white dark:bg-zinc-950 text-black dark:text-white transition-all duration-300 hover:bg-accent hover:text-black ${isCompact ? 'px-2.5 py-1.5 scale-95' : 'px-3 py-2 scale-100'}`}
      whileTap={{ scale: 0.9 }}
    >
      <motion.span
        key={theme}
        initial={{ rotate: -90, opacity: 0 }}
        animate={{ rotate: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 400, damping: 25 }}
        className="inline-flex"
      >
        {theme === 'dark' ? (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-5 w-5 fill-current"><path d="M6.76 4.84l-1.8-1.79-1.41 1.41 1.79 1.8 1.42-1.42zm10.48 0l1.79-1.8 1.41 1.41-1.8 1.79-1.4-1.4zM12 4V1h0v3h0zm0 19v-3h0v3h0zM4 13H1v0h3v0zm19 0h-3v0h3v0zM6.76 19.16l-1.42 1.42-1.79-1.8 1.41-1.41 1.8 1.79zM19.16 17.24l1.41 1.41-1.79 1.8-1.42-1.42 1.8-1.79zM12 6a6 6 0 100 12A6 6 0 0012 6z"/></svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-5 w-5 fill-current"><path d="M21.64 13a9 9 0 11-10.63-10.6 7 7 0 1010.63 10.6z"/></svg>
        )}
      </motion.span>
      <span className="sr-only">Toggle theme</span>
    </motion.button>
  )
}

function MobileMenuButton({ isMenuOpen, setIsMenuOpen }) {
  return (
    <div className="md:hidden flex items-center gap-2">
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="inline-flex items-center justify-center p-2 border-2 border-black dark:border-white text-black dark:text-white transition-colors hover:bg-accent hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        aria-controls="mobile-menu"
        aria-expanded={isMenuOpen}
        aria-label="Toggle menu"
      >
        <motion.svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          animate={isMenuOpen ? 'open' : 'closed'}
          variants={{
            open: { rotate: 90 },
            closed: { rotate: 0 }
          }}
          transition={{ duration: 0.3 }}
        >
          {isMenuOpen ? (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          ) : (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          )}
        </motion.svg>
      </button>
    </div>
  )
}

function MobileMenu({ isMenuOpen, setIsMenuOpen, nav, location, openDropdown, setOpenDropdown, handleAnchorClick, theme, setTheme, setCommandOpen }) {
  return (
    <AnimatePresence>
      {isMenuOpen && (
        <motion.div
          key="mobile-menu"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          className="md:hidden bg-white dark:bg-zinc-950 px-4 pt-2 pb-4 space-y-2 overflow-hidden border-t-2 border-black dark:border-white"
          aria-label="Mobile navigation"
        >
          {nav.customLinks?.filter(link => link.show).map((link, i) => (
            <MobileNavItem
              key={i}
              link={link}
              index={i}
              location={location}
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
              handleAnchorClick={handleAnchorClick}
              setIsMenuOpen={setIsMenuOpen}
            />
          ))}
          <button
            onClick={() => { setCommandOpen(true); setIsMenuOpen(false); }}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 border-2 border-black dark:border-white font-mono text-sm text-black dark:text-white transition-colors hover:bg-accent hover:text-black"
          >
            Open command palette
          </button>
          <button
            onClick={() => { setTheme(theme === 'dark' ? 'light' : 'dark'); setIsMenuOpen(false); }}
            aria-label="Toggle dark mode"
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 border-2 border-black dark:border-white font-mono text-sm text-black dark:text-white transition-colors hover:bg-accent hover:text-black"
          >
            {theme === 'dark' ? 'Switch to Light' : 'Switch to Dark'}
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function MobileNavItem({ link, index, location, openDropdown, setOpenDropdown, handleAnchorClick, setIsMenuOpen }) {
  const isActive = location.pathname === '/' + link.slug
  const hasChildren = link.children?.length > 0

  if (hasChildren) {
    return (
      <div>
        <button
          onClick={() => setOpenDropdown(openDropdown === index ? null : index)}
          className="w-full text-left flex items-center justify-between text-black dark:text-white px-2 py-2 font-mono transition-colors duration-200 capitalize hover:text-accent font-semibold"
        >
          {link.label}
          <ChevronDownIcon
            className={`h-4 w-4 transition-transform duration-200 ${openDropdown === index ? 'rotate-180' : ''}`}
          />
        </button>
        {openDropdown === index && (
          <div className="pl-4 space-y-1">
            {link.children.map((child, cidx) => (
              <Link
                key={cidx}
                to={`/${child.slug}`}
                onClick={() => {
                  handleAnchorClick({ preventDefault: () => {} }, child.slug)
                  setIsMenuOpen(false)
                }}
                className="block text-sm text-zinc-700 dark:text-zinc-300 px-2 py-1.5 font-mono capitalize transition-colors hover:text-accent"
              >
                {child.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <Link
      to={`/${link.slug}`}
      onClick={(e) => {
        handleAnchorClick(e, `/${link.slug}`)
        setIsMenuOpen(false)
      }}
      className={`w-full block text-left text-black dark:text-white px-2 py-2 font-mono transition-colors duration-200 capitalize hover:text-accent ${isActive ? 'font-semibold text-accent' : ''}`}
    >
      {link.label}
    </Link>
  )
}
